/**
 * My team's training — TRUS-562, widened by TRUS-592.
 *
 * "For staff records, the managers of those individuals need a place to view
 * their staff records (in addition to the admin). ... The admin needs the
 * ability to view their own trainings as a staff as well as those who report
 * to them."
 *
 * TRUS-592 (Amigos): "managers should see everyone who reports to them, and if
 * those managers have direct reports they should see those as well." So the
 * team is the caller's WHOLE line, not one level. Each row says who the person
 * reports to, and direct reports are marked, so the deeper rows read as part
 * of a sub-team rather than as strangers.
 *
 * This sits beside My Trainings rather than in the admin Training hub on
 * purpose: it is the same person's own view of their own obligations, one
 * level out. An admin opening it sees THEIR line, not the org — the org view
 * is Compliance, and it stays admin-only.
 *
 * There is no staff picker here and there must never be one. The server
 * derives the team from the reporting line, so this screen cannot ask about
 * someone it is not entitled to; adding a filter would move that decision to
 * the client, which is where it stops being a boundary.
 *
 * Someone who reports to you and has NOTHING assigned appears, called out
 * rather than omitted. That is the row a manager most needs to see, and a
 * screen that only lists people with assignments hides it perfectly.
 *
 * Expanding a row shows everything about that person's training (TRUS-562):
 * every assigned training with its due date and status, and their completion
 * records. The records also need the `training.view_team_records` function on
 * the caller's role (admins skip it); until it is granted the assignments still
 * show and the records section says what to ask for.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Clock, User, Users } from 'lucide-react'
import { PageHeader, PageContainer, PageState, SectionHeader } from '@/components/weavein'
import { Button, ErrorNote, FILTER_INPUT, FilterBar, Modal, Pill, SortTh, TableShell, Td, Th, useSort } from './ui'
import PersonTrainingPanel from './PersonTrainingPanel'
import {
  StaffProfile,
  TeamCompliance,
  formatDate,
  getStaffProfile,
  myTeamCompliance,
} from './api'

const OPEN_STATUSES = ['overdue', 'due_soon', 'pending'] as const
const COLUMNS = 8

function openCount(byStatus: Record<string, number>): number {
  return OPEN_STATUSES.reduce((n, s) => n + (byStatus[s] || 0), 0)
}

const MANAGER_FIELD = 'staff_manager_name'

export default function MyTeamPage() {
  const [data, setData] = useState<TeamCompliance | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [profileFor, setProfileFor] = useState<number | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await myTeamCompliance())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your team.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const toggle = (userId: number) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }

  const team = data?.team ?? []
  const totals = data?.totals ?? {}
  const directCount = team.filter((m) => m.is_direct_report).length

  // TRUS-562: search, filter and sort the team.
  const [search, setSearch] = useState('')
  const [show, setShow] = useState('')
  const filteredTeam = team.filter((m) => {
    const needle = search.trim().toLowerCase()
    if (needle && !`${m.name ?? ''} ${m.email ?? ''}`.toLowerCase().includes(needle)) return false
    if (show === 'overdue' && !(m.by_status.overdue > 0)) return false
    if (show === 'due_soon' && !(m.by_status.due_soon > 0)) return false
    if (show === 'nothing' && Object.keys(m.by_status).length > 0) return false
    if (show === 'direct' && !m.is_direct_report) return false
    return true
  })
  // `toggle` is taken (it expands a person's row), so the sort handler is toggleSort.
  const { sorted: shownTeam, sort, toggle: toggleSort } = useSort(
    filteredTeam,
    {
      name: (m) => m.name,
      reports_to: (m) => (m.is_direct_report ? '' : m.reports_to?.name),
      overdue: (m) => m.by_status.overdue || 0,
      due_soon: (m) => m.by_status.due_soon || 0,
      open: (m) => openCount(m.by_status),
      completed: (m) => m.by_status.completed || 0,
      earliest: (m) => m.earliest_due,
    },
    // Most overdue first, as the server orders it.
    { key: 'overdue', dir: 'desc' },
  )

  // Names for everyone this page knows about, so the profile view can show the
  // Manager field as a name rather than the user id it stores.
  const knownNames = useMemo(() => {
    const names = new Map<number, string>()
    if (data?.manager_user_id) names.set(data.manager_user_id, 'You')
    for (const m of team) {
      if (m.name) names.set(m.user_id, m.name)
      if (m.reports_to?.name) names.set(m.reports_to.user_id, m.reports_to.name)
    }
    return names
  }, [team, data?.manager_user_id])

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Training · My team"
        title="My team"
        description="Training owed by everyone who reports to you — directly, or through a manager who reports to you."
      />

      {error && <ErrorNote message={error} onDismiss={() => setError(null)} />}

      {loading ? (
        <PageState variant="loading" title="Loading your team" />
      ) : team.length === 0 ? (
        <PageState
          variant="empty"
          icon={<Users className="h-5 w-5 text-ink-400" />}
          title="Nobody reports to you yet"
          message="Reporting lines come from the Manager field on each staff profile. If someone should be on this list, an administrator can set you as their manager."
        />
      ) : (
        <>
          <div className="mb-4 flex flex-wrap gap-2">
            {totals.overdue ? (
              <Pill tone="danger">
                <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
                {totals.overdue} overdue
              </Pill>
            ) : null}
            {totals.due_soon ? (
              <Pill tone="attention">
                <Clock className="mr-1 inline h-3.5 w-3.5" />
                {totals.due_soon} due soon
              </Pill>
            ) : null}
            {totals.completed ? (
              <Pill tone="success">
                <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" />
                {totals.completed} completed
              </Pill>
            ) : null}
          </div>

          <SectionHeader
            title={`${team.length} ${team.length === 1 ? 'person' : 'people'}`}
            description={
              directCount < team.length
                ? `${directCount} report to you directly; ${team.length - directCount} through another manager.`
                : undefined
            }
          />
          <FilterBar>
            <input
              type="search"
              className={`${FILTER_INPUT} min-w-[200px] flex-1`}
              placeholder="Search your team"
              aria-label="Search your team"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select className={FILTER_INPUT} aria-label="Show" value={show} onChange={(e) => setShow(e.target.value)}>
              <option value="">Everyone</option>
              <option value="overdue">Has overdue training</option>
              <option value="due_soon">Has training due soon</option>
              <option value="nothing">Nothing assigned</option>
              <option value="direct">Direct reports only</option>
            </select>
          </FilterBar>
          <TableShell>
            <thead>
              <tr>
                <SortTh label="Name" sortKey="name" sort={sort} onSort={toggleSort} />
                <SortTh label="Reports to" sortKey="reports_to" sort={sort} onSort={toggleSort} />
                <SortTh label="Overdue" sortKey="overdue" sort={sort} onSort={toggleSort} />
                <SortTh label="Due soon" sortKey="due_soon" sort={sort} onSort={toggleSort} />
                <SortTh label="Open" sortKey="open" sort={sort} onSort={toggleSort} />
                <SortTh label="Completed" sortKey="completed" sort={sort} onSort={toggleSort} />
                <SortTh label="Earliest due" sortKey="earliest" sort={sort} onSort={toggleSort} />
                <Th className="text-right">
                  <span className="sr-only">Actions</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {shownTeam.length === 0 && (
                <tr>
                  <Td colSpan={COLUMNS} className="text-ink-500">
                    No one on your team matches.
                  </Td>
                </tr>
              )}
              {shownTeam.map((m) => {
                const overdue = m.by_status.overdue || 0
                const nothingAssigned = Object.keys(m.by_status).length === 0
                const isOpen = expanded.has(m.user_id)
                return (
                  <React.Fragment key={m.user_id}>
                    <tr>
                      <Td>
                        <button
                          type="button"
                          onClick={() => toggle(m.user_id)}
                          aria-expanded={isOpen}
                          className="flex items-start gap-1 text-left"
                          title="Show this person's training"
                        >
                          {isOpen ? (
                            <ChevronDown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-400" />
                          ) : (
                            <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-400" />
                          )}
                          <span>
                            <span className="block font-medium text-ink">{m.name || `User ${m.user_id}`}</span>
                            <span className="block text-[12px] text-ink-400">{m.email}</span>
                          </span>
                        </button>
                      </Td>
                      <Td className="text-ink-500">
                        {m.is_direct_report ? (
                          <Pill tone="accent">You</Pill>
                        ) : (
                          m.reports_to?.name || <span className="text-ink-400">—</span>
                        )}
                      </Td>
                      {nothingAssigned ? (
                        <Td className="text-ink-500" colSpan={5}>
                          {/* Not an empty row: somebody with no training assigned
                              is the finding, and silence would read as "fine". */}
                          <Pill tone="attention">No training assigned</Pill>
                        </Td>
                      ) : (
                        <>
                          <Td>
                            {overdue > 0 ? <Pill tone="danger">{overdue}</Pill> : <span className="text-ink-400">—</span>}
                          </Td>
                          <Td>{m.by_status.due_soon || <span className="text-ink-400">—</span>}</Td>
                          <Td>{openCount(m.by_status) || <span className="text-ink-400">—</span>}</Td>
                          <Td>{m.by_status.completed || <span className="text-ink-400">—</span>}</Td>
                          <Td className="text-ink-500">
                            {m.earliest_due ? formatDate(m.earliest_due) : <span className="text-ink-400">—</span>}
                          </Td>
                        </>
                      )}
                      <Td className="text-right">
                        <Button variant="ghost" onClick={() => setProfileFor(m.user_id)}>
                          <User className="h-3.5 w-3.5" />
                          Profile
                        </Button>
                      </Td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <Td colSpan={COLUMNS} className="bg-paper-2">
                          <PersonTrainingPanel userId={m.user_id} />
                        </Td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </TableShell>
        </>
      )}

      <ProfileModal userId={profileFor} knownNames={knownNames} onClose={() => setProfileFor(null)} />
    </PageContainer>
  )
}

function displayValue(field: { name: string; value: unknown }, knownNames: Map<number, string>): string {
  const v = field.value
  if (v === null || v === undefined || v === '') return '—'
  // The Manager field stores a user id; show the person.
  if (field.name === MANAGER_FIELD && /^\d+$/.test(String(v))) {
    return knownNames.get(Number(v)) ?? `User ${v}`
  }
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—'
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

/** Read-only. Editing a profile stays on the admin Staff Profiles page. */
function ProfileModal({
  userId,
  knownNames,
  onClose,
}: {
  userId: number | null
  knownNames: Map<number, string>
  onClose: () => void
}) {
  const [profile, setProfile] = useState<StaffProfile | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (userId === null) return
    let cancelled = false
    setProfile(null)
    setError(null)
    getStaffProfile(userId)
      .then((p) => {
        if (!cancelled) setProfile(p)
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load this profile.')
      })
    return () => {
      cancelled = true
    }
  }, [userId])

  return (
    <Modal
      open={userId !== null}
      title={profile?.user.name || 'Staff profile'}
      description={profile?.user.email || undefined}
      onClose={onClose}
      wide
    >
      {error ? (
        <p className="text-danger">{error}</p>
      ) : !profile ? (
        <p className="text-ink-500">Loading profile…</p>
      ) : (
        <div className="space-y-5">
          {profile.groups.map((g) => {
            const filled = g.fields.filter((f) => displayValue(f, knownNames) !== '—')
            if (filled.length === 0) return null
            return (
              <section key={g.category}>
                <h3 className="mb-2 font-mono text-[10px] uppercase tracking-[0.14em] text-ink-500">{g.label}</h3>
                <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                  {filled.map((f) => (
                    <div key={f.id}>
                      <dt className="text-[12px] text-ink-400">{f.friendly_name || f.name}</dt>
                      <dd className="text-[13px] text-ink">{displayValue(f, knownNames)}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            )
          })}
          {/* TRUS-562: the employee record includes their training. */}
          <section>
            <h3 className="mb-2 font-mono text-[10px] uppercase tracking-[0.14em] text-ink-500">Training</h3>
            <PersonTrainingPanel userId={profile.user.id} />
          </section>
        </div>
      )}
    </Modal>
  )
}
